import { track } from '@tldraw/tldraw'
import { useDebounce } from 'hooks/useDebounce'
import { useStockVideos } from 'hooks/queries/designer/useStockVideos'
import { MediaFile } from 'components/Design/UploadsTab/MediaFile'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import Styles from '../UploadsTab.module.scss'
import { Button } from 'blaze-ui/Button'
import { useEffect, useState } from 'react'
import { Spinner } from 'common/Spinner/Spinner'
import { observer } from 'mobx-react-lite'
import { useInsertStockVideos } from '../useInsertStockVideos'
import { useRootStore } from 'stores/RootStore'

interface Props {
  query: string
}

const VIDEOS_TO_SHOW_IN_PREVIEW = 6
const PAGE_SIZE = 18

export const getVideoMimeType = (url: string) => {
  const fileType = url.split('.').pop()

  return `video/${fileType}`
}

export const StockVideosGallery = track(
  observer(({ query }: Props) => {
    const debouncedQuery = useDebounce(query, 500)
    const { isLoading, data, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useStockVideos({
      query: debouncedQuery,
      pageSize: PAGE_SIZE,
      videoType: 'all',
    })
    const [seeMore, setSeeMore] = useState(false)
    const { handleInsert } = useInsertStockVideos()
    const { designStore } = useRootStore()
    const file = designStore?.file
    const designAspectRatio = file?.width && file?.height ? `${file.width} / ${file.height}` : '16 / 9'

    useEffect(() => setSeeMore(false), [query])

    if (isLoading || !data || error || !data.pages[0].videos.length) {
      return null
    }

    const videos = data.pages.flatMap((page) => page.videos)

    return (
      <>
        <div className={Styles.stockVideosGroupItems}>
          {videos.slice(0, seeMore ? videos.length : VIDEOS_TO_SHOW_IN_PREVIEW).map((video) => (
            <div key={video.id} className={Styles.stockVideoItem} style={{ aspectRatio: designAspectRatio }}>
              <MediaFile
                src={video.videos.medium.thumbnail}
                file={
                  {
                    id: video.id,
                    source_url: video.videos.medium.url,
                    duration: video.duration,
                    variants: [],
                    mime_type: video.videos.medium.mime_type ?? getVideoMimeType(video.videos.medium.url),
                    title: video.id,
                  } as IMediaFile
                }
                onPress={() => handleInsert(video)}
              />
            </div>
          ))}
        </div>
        {isFetchingNextPage && <Spinner />}
        {query && (hasNextPage || (!seeMore && videos.length > VIDEOS_TO_SHOW_IN_PREVIEW)) && (
          <Button
            variant="secondary"
            onPress={() => {
              setSeeMore(true)
              hasNextPage && fetchNextPage()
            }}
          >
            Show more
          </Button>
        )}
      </>
    )
  }),
)
